import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getRoofState, getSettingsState, getWallState } from '../../../store/slices/selectors'
import { wallActions } from '../../../store/slices'

import { FastField } from 'formik'

import {
  Accordion,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
  RangeSlider,
} from '../../../../../components'

import { RenderAorB, RenderIf } from '../../../../../utils/fns'
import { useLazyEffect } from '../../../../../utils/hooks'

import {
  APPLICATION_TYPES,
  STUD_ALIGNMENT_MARKS,
  STUD_ALIGNMENT_RANGE,
} from '../../../common/constants'
import {
  memberTypeOptions,
  typeOptions,
  topPlateThicknessOptions,
  topPlateWidthOptions,
  topPlatePliesOptions,
  studPliesOptions,
  studThicknessOptions,
} from '../../../common/json/carried-member.json'
import { defaultValues, helpText } from '../../../common/json/common.json'
import { getRoofWidthOptions } from '../../../common/fns'

const getOptionsBy = (obj, key) => obj[key] ?? obj.default

const Wall = () => {
  const dispatch = useDispatch()

  const {
    applicationType,
    memberType,
    type,
    topPlateThickness,
    topPlateWidth,
    topPlatePlies,
    studPlies,
    studThickness,
    studAlignment,
    isStudMember,
  } = useSelector(getWallState)

  const roofWidth = useSelector((state) => getRoofState(state).width)
  const roofPlies = useSelector((state) => getRoofState(state).ply)
  const roofMemberType = useSelector((state) => getRoofState(state).memberType)
  const roofType = useSelector((state) => getRoofState(state).type)
  const country = useSelector((state) => getSettingsState(state).country)

  const options = useMemo(() => {
    const lumberSpecies = typeOptions[memberType]
      ? typeOptions[memberType][country]
      : typeOptions.default
    const topPlateWidth = getOptionsBy(topPlateWidthOptions, memberType)
    const topPlateThickness = getOptionsBy(topPlateThicknessOptions, memberType)

    return {
      lumberSpecies,
      topPlateWidth,
      topPlateThickness,
    }
  }, [memberType, country])

  const roofWidthOptions = useMemo(() => {
    const options = getRoofWidthOptions(roofMemberType, roofType)

    return options
  }, [roofMemberType, roofType])

  const studThicknessLabel = useMemo(() => {
    if (!studAlignment) return
    const selectedWidth = roofWidthOptions.find((o) => o.value === studThickness)
    return selectedWidth ? selectedWidth.text : selectedWidth
  }, [studThickness, studAlignment, roofWidthOptions])

  const handleChange = (e) =>
    dispatch(wallActions.modifyCalculation({ name: e.target.name, value: e.target.value }))

  const handleChangeMemberType = (e) => {
    dispatch(
      wallActions.updateCalculation({
        [e.target.name]: e.target.value,
        ...defaultValues.wall[e.target.value],
      }),
    )
  }

  const handleChangeLumberSpecies = (e) => {
    const { name, value, options } = e.target

    const selectedOption = options.find((opt) => opt.value === value)

    dispatch(
      wallActions.updateCalculation({
        [name]: value,
        lumberSpecies: selectedOption.text,
      }),
    )
  }

  const handleChangeStudAlignmentOffset = (e) => {
    const { value } = e.target
    const isStudAlignment = Number(value) === 0

    const updatedStudValues = Object.assign(
      {
        [e.target.name]: value,
        studAlignment: isStudAlignment,
      },
      !isStudAlignment && { studThickness: 1.5, studPlies: 1 },
    )

    dispatch(wallActions.updateCalculation(updatedStudValues))
  }

  useLazyEffect(() => {
    if (studAlignment) {
      dispatch(
        wallActions.modifyCalculation({
          name: 'studThickness',
          value: roofWidth,
        }),
      )
    }
  }, [studAlignment, roofWidth])

  useLazyEffect(() => {
    if (studAlignment) {
      dispatch(
        wallActions.modifyCalculation({
          name: 'studPlies',
          value: roofPlies,
        }),
      )
    }
  }, [studAlignment, roofPlies])

  return (
    <Accordion title="Wall (Carrying Member)">
      <div className="grid grid__auto--200 grid--gap-1-2">
        <FormGroup>
          <FormLabel htmlFor="memberType">Wall Type</FormLabel>
          <Dropdown
            name="memberType"
            value={memberType}
            options={memberTypeOptions[applicationType] ?? memberTypeOptions.default}
            onChange={handleChangeMemberType}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="type">Lumber Species</FormLabel>
          <Dropdown
            name="type"
            value={type}
            options={options.lumberSpecies}
            onChange={handleChangeLumberSpecies}
            disabled={!isStudMember}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="topPlateThickness">
            {isStudMember ? 'Top Plate Thickness' : 'Depth'}
          </FormLabel>
          <Dropdown
            name="topPlateThickness"
            value={topPlateThickness}
            onChange={handleChange}
            options={options.topPlateThickness}
            disabled={!isStudMember}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel htmlFor="topPlateWidth">
            {isStudMember ? 'Top Plate Width' : 'Width'}
          </FormLabel>
          <Dropdown
            name="topPlateWidth"
            value={topPlateWidth}
            options={options.topPlateWidth}
            onChange={handleChange}
          />
        </FormGroup>
        {RenderIf(
          isStudMember,
          <FormGroup>
            <FormLabel htmlFor="topPlatePlies">Top Plate Plies</FormLabel>
            <Dropdown
              name="topPlatePlies"
              value={topPlatePlies}
              onChange={handleChange}
              options={topPlatePliesOptions}
              disabled={applicationType !== APPLICATION_TYPES.PERPENDICULAR_TO_WALL}
            />
          </FormGroup>,
        )}
        {RenderIf(
          applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL && isStudMember,
          <FormGroup>
            <FormLabel htmlFor="studPlies">Stud Plies</FormLabel>
            {RenderAorB(
              studAlignment,
              <FormControl value={studPlies} disabled />,
              <Dropdown
                name="studPlies"
                value={studPlies}
                onChange={handleChange}
                options={studPliesOptions}
              />,
            )}
          </FormGroup>,
        )}
        {RenderIf(
          applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL && isStudMember,
          <FormGroup>
            <FormLabel htmlFor="studThickness">Stud Thickness</FormLabel>
            {RenderAorB(
              studAlignment,
              <FormControl value={studThicknessLabel} disabled />,
              <Dropdown
                name="studThickness"
                value={studThickness}
                onChange={handleChange}
                options={studThicknessOptions}
              />,
            )}
          </FormGroup>,
        )}
      </div>
      {RenderIf(
        applicationType === APPLICATION_TYPES.PERPENDICULAR_TO_WALL && isStudMember,
        <div className="grid grid__auto--200 grid--gap-1-2">
          <FormGroup>
            <FormLabel htmlFor="studAlignmentOffset" popperText={helpText.studAlignment}>
              Stud Alignment
            </FormLabel>
            <FastField name="studAlignmentOffset">
              {({ field, meta }) => (
                <RangeSlider
                  {...field}
                  addonBefore="Offset LEFT"
                  addonAfter="Offset RIGHT"
                  onChange={handleChangeStudAlignmentOffset}
                  min={STUD_ALIGNMENT_RANGE.MIN}
                  max={STUD_ALIGNMENT_RANGE.MAX}
                  step={STUD_ALIGNMENT_RANGE.STEP}
                  marks={STUD_ALIGNMENT_MARKS}
                  disabled={!isStudMember}
                  error={meta.error}
                />
              )}
            </FastField>
          </FormGroup>
        </div>,
      )}
    </Accordion>
  )
}

export default Wall
